import {
  Calendar,
  FileText,
  Layout,
  MapPin,
  Rocket,
  ShieldCheck,
  Smartphone,
  Target,
  VideoOff,
} from "lucide-react";
import React from "react";
import { useTranslation } from "../../../contexts/LanguageContext";
import { useAds } from "../hooks/AdsContext";
import {
  extractPostMediaUrl,
  getCtaLabel,
  getYoutubeId,
} from "../utils/adsUtils";
import { getPlatformName } from "../../../utils/platformIcons";

const Step4_Review: React.FC = () => {
  const { t } = useTranslation();
  const [mediaError, setMediaError] = React.useState(false);
  const {
    name,
    platform,
    dailyBudget,
    budgetMode,
    costPerResultGoal,
    startDate,
    endDate,
    destinationType,
    customLocations,
    ageMin,
    ageMax,
    gender,
    interests,
    keywords,
    leadFields,
    standardLeadFields,
    selectedApprovedPost,
    whatsappPhone,
    whatsappOfficial,
    privacyPolicyUrl,
    useMetaLookalike,
    useGoogleCustomerMatch,
    ctaText,
    metaPlatform,
    sitelinks,
    postMediaUrls,
    youtubeUrls,
    trackingParam,
    linkedinJobTitles,
    linkedinIndustries,
    linkedinSkills,
    linkedinCompanySizes,
    linkedinJobFunctions,
    linkedinMatchedAudiences,
    linkedinObjective,
    linkedinFormName,
    linkedinAccounts,
    selectedLinkedinAccount,
    startTime,
    endTime,
    radiusKm,
    googleCampaignType,
    metaLookalikeSource,
    metaLookalikePercent,
    useMetaCustomAudience,
    metaCustomAudienceName,
    headline,
    message,
    description,
    linkUrl,
    businessName,
    linkedinLanguage,
    customFields,
    contextHints,
    chatgptObjective,
  } = useAds();

  // Fix timezone shift for date display
  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    // Adding T12:00:00 ensures we stay on the same calendar day regardless of local timezone (up to +/- 12h)
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString("pt-BR");
  };

  const [currentMediaIndex, setCurrentMediaIndex] = React.useState(0);

  const periodDays =
    startDate && endDate
      ? Math.max(
          1,
          Math.ceil(
            (new Date(endDate).getTime() - new Date(startDate).getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : 1;
  const dailyBudgetNum = Number(String(dailyBudget).replace(",", "."));
  const totalInvest = (dailyBudgetNum * periodDays).toLocaleString(
    platform === "chatgpt_ads" ? "en-US" : "pt-BR",
    {
      style: "currency",
      currency: platform === "chatgpt_ads" ? "USD" : "BRL",
    },
  );
  const media = selectedApprovedPost
    ? extractPostMediaUrl(selectedApprovedPost)
    : null;

  const allMedia =
    postMediaUrls.length > 0 ? postMediaUrls : media ? [media] : [];

  const platformLabel = (() => {
    if (platform === "meta") {
      const delivery =
        metaPlatform === "both"
          ? t("FB + IG")
          : metaPlatform === "facebook"
            ? t("Somente FB")
            : t("Somente IG");
      return `${getPlatformName("meta")} (${delivery})`;
    }
    return getPlatformName(platform);
  })();

  const adsManagerLabel =
    platform === "meta"
      ? `${getPlatformName("facebook")}/${getPlatformName("instagram")}`
      : platform === "google_ads"
        ? getPlatformName("google_ads")
        : platform === "linkedin"
          ? getPlatformName("linkedin")
          : platform === "chatgpt_ads"
            ? getPlatformName("chatgpt_ads")
            : t("TikTok Ads Manager");

  const summaryCards = [
    {
      title: t("Configurações Básicas"),
      icon: Layout,
      color: "text-blue-600",
      bg: "bg-blue-50",
      details: [
        { label: t("Campanha"), value: name || t("Sem nome") },
        {
          label: t("Plataforma"),
          value: platformLabel,
        },
        ...(platform === "google_ads"
          ? [
              {
                label: t("Tipo de Campanha"),
                value:
                  {
                    search: t("Rede de Pesquisa"),
                    display: t("Rede de Display"),
                    video: t("YouTube Vídeo"),
                    performance_max: t("Performance Max (IA)"),
                    shopping: t("Shopping"),
                  }[googleCampaignType] || googleCampaignType,
              },
              {
                label: t("Nome da Empresa"),
                value: businessName || t("Não informado"),
              },
            ]
          : []),
        ...(platform === "linkedin"
          ? [
              {
                label: t("Conta de Anúncios"),
                value:
                  linkedinAccounts.find(
                    (a: any) => a.id === selectedLinkedinAccount,
                  )?.name || t("Não selecionada"),
              },
            ]
          : []),
        {
          label: t("Objetivo"),
          value:
            platform === "chatgpt_ads"
              ? chatgptObjective === "conversions"
                ? t("Conversões (otimizado)")
                : t("Cliques (lance fixo)")
              : destinationType === "whatsapp"
                ? t("Lead via WhatsApp")
                : destinationType === "form"
                  ? t("Lead via Formulário")
                  : platform === "linkedin"
                    ? {
                        awareness: t("Reconhecimento de Marca"),
                        traffic: t("Visitas ao Site"),
                        engagement: t("Engajamento"),
                        leads: t("Geração de Leads"),
                        conversion: t("Conversões no Site"),
                      }[linkedinObjective] || t("Tráfego Site")
                    : t("Tráfego Site"),
        },
        { label: t("Botão (CTA)"), value: getCtaLabel(ctaText, t) },
        {
          label: t("Destino"),
          value:
            destinationType === "whatsapp"
              ? `${whatsappPhone} (${whatsappOfficial ? t("API Oficial") : t("Link wa.me")})`
              : destinationType === "form" ||
                  (platform === "linkedin" && linkedinObjective === "leads")
                ? linkedinFormName || t("Formulário de Leads")
                : linkUrl || t("Não informado"),
        },
      ],
    },
    {
      title: t("Texto do Anúncio"),
      icon: FileText,
      color: "text-indigo-600",
      bg: "bg-indigo-50",
      details: [
        {
          label: t("Título de Destaque"),
          value: headline || t("Não preenchido"),
        },
        {
          label: t("Copy Principal"),
          value: message || t("Não preenchido"),
        },
        {
          label: t("Subtítulo Descritivo"),
          value: description || t("Não preenchido"),
        },
      ],
    },
    {
      title:
        platform === "chatgpt_ads"
          ? t("Contexto de Conversa")
          : t("Público & Alcance"),
      icon: Target,
      color: "text-purple-600",
      bg: "bg-purple-50",
      details:
        platform === "chatgpt_ads"
          ? [
              {
                label: t("Palavras-chave de Contexto"),
                value:
                  contextHints.length > 0
                    ? contextHints.join(", ")
                    : t("Nenhuma definida"),
              },
            ]
          : [
              {
                label: t("Segmentação por Idade"),
                value: t("{min}-{max} anos", { min: ageMin, max: ageMax }),
              },
              {
                label: t("Gênero"),
                value:
                  gender === "all"
                    ? t("Todos")
                    : gender === "male"
                      ? t("Homens")
                      : t("Mulheres"),
              },
              ...(platform === "linkedin" && linkedinLanguage
                ? [
                    {
                      label: t("Idioma do Perfil"),
                      value:
                        linkedinLanguage.split(":").pop() || linkedinLanguage,
                    },
                  ]
                : []),
              {
                label: t("Segmentos / Critérios"),
                value:
                  platform === "google_ads"
                    ? keywords.length > 0
                      ? t("{count} keywords", { count: keywords.length })
                      : t("Amplo")
                    : platform === "linkedin"
                      ? [
                          linkedinJobTitles.length > 0 &&
                            t("{count} cargos", {
                              count: linkedinJobTitles.length,
                            }),
                          linkedinIndustries.length > 0 &&
                            t("{count} setores", {
                              count: linkedinIndustries.length,
                            }),
                          linkedinSkills.length > 0 &&
                            t("{count} skills", {
                              count: linkedinSkills.length,
                            }),
                          linkedinCompanySizes.length > 0 &&
                            t("{count} tamanhos de empresa", {
                              count: linkedinCompanySizes.length,
                            }),
                          linkedinJobFunctions.length > 0 &&
                            t("{count} funções", {
                              count: linkedinJobFunctions.length,
                            }),
                        ]
                          .filter(Boolean)
                          .join(" • ") || t("Amplo")
                      : interests.length > 0
                        ? t("{count} interesses", { count: interests.length })
                        : t("Amplo"),
              },
              {
                label: t("Audiência Avançada"),
                value:
                  platform === "linkedin"
                    ? linkedinMatchedAudiences.length > 0
                      ? t("{count} públicos Matched", {
                          count: linkedinMatchedAudiences.length,
                        })
                      : t("Desativada")
                    : useMetaLookalike
                      ? t("Lookalike {percent}% ({source})", {
                          percent: metaLookalikePercent,
                          source:
                            metaLookalikeSource === "pixel_event"
                              ? t("Pixel Meta")
                              : t("Leads Virtus"),
                        })
                      : useMetaCustomAudience
                        ? t("Personalizado: {name}", {
                            name: metaCustomAudienceName || t("sem nome"),
                          })
                        : useGoogleCustomerMatch
                          ? t("Google Customer Match")
                          : t("Desativada"),
              },
            ],
    },
    {
      title: t("Orçamento & Período"),
      icon: Calendar,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      details: [
        {
          label: t("Investimento Diário"),
          value: `${platform === "chatgpt_ads" ? "US$" : "R$"} ${dailyBudget || "0"}`,
        },
        ...(platform === "meta"
          ? [
              {
                label: t("Modo de Orçamento"),
                value:
                  budgetMode === "CBO"
                    ? t("CBO (na campanha)")
                    : t("ABO (por conjunto)"),
              },
              {
                label: t("Meta de Custo por Resultado"),
                value: costPerResultGoal
                  ? `R$ ${costPerResultGoal}`
                  : t("Não definida (otimiza por volume)"),
              },
            ]
          : []),
        {
          label: t("Horário de Veiculação"),
          value: t("{start} às {end}", { start: startTime, end: endTime }),
        },
        {
          label: t("Duração"),
          value: t("{days} dias", { days: periodDays }),
        },
        {
          label: t("Início"),
          value: formatDisplayDate(startDate),
        },
        { label: t("Fim"), value: formatDisplayDate(endDate) },
        {
          label: t("Parâmetros UTM"),
          value: trackingParam
            ? t("{count} parâmetro(s) configurado(s)", {
                count: trackingParam.split("&").filter(Boolean).length,
              })
            : t("Padrão automático"),
          tooltip: trackingParam || undefined,
        },
      ],
    },
  ];

  const isLeadFormDestination =
    destinationType === "form" ||
    (platform === "linkedin" && linkedinObjective === "leads");

  if (isLeadFormDestination) {
    const fieldLabels = leadFields
      .map(
        (id) => standardLeadFields.find((f: any) => f.id === id)?.label || id,
      )
      .join(", ");
    summaryCards.push({
      title: t("Formulário de Leads"),
      icon: FileText,
      color: "text-purple-600",
      bg: "bg-purple-50",
      details: [
        {
          label: t("Campos de Coleta"),
          value: fieldLabels || t("Nenhum campo selecionado"),
        },
        ...(customFields.length > 0
          ? [
              {
                label: t("Perguntas Customizadas"),
                value: customFields.map((f: any) => f.label).join(", "),
              },
            ]
          : []),
        {
          label: t("Política de Privacidade"),
          value: privacyPolicyUrl || t("Não informado"),
        },
      ],
    });
  }

  if (
    platform === "google_ads" &&
    sitelinks &&
    sitelinks.some((s) => s.title)
  ) {
    summaryCards.push({
      title: t("Sitelinks (Google)"),
      icon: Rocket,
      color: "text-blue-500",
      bg: "bg-blue-50",
      details: sitelinks
        .filter((s) => s.title)
        .map((s, i) => ({
          label: t("Sitelink #{index}", { index: i + 1 }),
          value: [s.title, s.description1, s.description2]
            .filter(Boolean)
            .join(" · "),
        })),
    });
  }

  if (
    platform === "google_ads" &&
    youtubeUrls &&
    youtubeUrls.some((url) => url.trim())
  ) {
    summaryCards.push({
      title: t("Vídeos (YouTube)"),
      icon: Rocket,
      color: "text-red-500",
      bg: "bg-red-50",
      details: youtubeUrls
        .filter((url) => url.trim())
        .map((url, i) => ({
          label: t("Vídeo #{index}", { index: i + 1 }),
          value: url,
        })),
    });
  }

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-3 duration-400">
      {/* Top Banner */}
      <div className="flex items-center justify-between bg-white p-3.5 px-5 rounded-2xl border border-purple-200/70 shadow-xs">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-purple-600 rounded-lg text-white shadow-xs">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="h-8 flex flex-col justify-between">
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
              {t("Resumo Tático & Lançamento")}
            </h2>
            <p className="text-[10px] text-gray-400 font-medium leading-none">
              {t("Revise os parâmetros finais antes de publicar.")}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">
            {t("Investimento Total")}
          </div>
          <div className="text-base font-extrabold text-purple-700 leading-none mt-0.5">
            {totalInvest}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-stretch">
        {/* Visual Preview Card (Shrink-wrapped to image) */}
        <div className="bg-white border border-purple-200/70 rounded-2xl p-4 flex flex-col shadow-xs relative overflow-hidden gap-3">
          <div className="space-y-3 flex-1">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              {t("Criativo Selecionado")}
            </div>

            {allMedia.length > 0 && !mediaError ? (
              <div className="relative group w-full aspect-4/5 rounded-xl overflow-hidden border border-purple-100 bg-purple-50/20">
                {(() => {
                  const currentMedia =
                    allMedia[currentMediaIndex % allMedia.length];
                  if (currentMedia.type === "image") {
                    return (
                      <img
                        src={currentMedia.url}
                        alt="Preview"
                        className="w-full h-full object-contain bg-black"
                        onError={() => setMediaError(true)}
                      />
                    );
                  } else {
                    const isYoutube =
                      currentMedia.url.includes("youtube.com") ||
                      currentMedia.url.includes("youtu.be");
                    if (isYoutube) {
                      const ytId = getYoutubeId(currentMedia.url);
                      return ytId ? (
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${ytId}`}
                          title="YouTube video player"
                          frameBorder="0"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-purple-300 bg-purple-50/20">
                          <VideoOff className="w-6 h-6" />
                          <span className="text-[10px] font-bold uppercase">
                            {t("Link do YouTube inválido")}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <video
                        src={currentMedia.url}
                        className="w-full h-full object-contain bg-black"
                        autoPlay
                        muted
                        loop
                        controls
                        onError={() => setMediaError(true)}
                      />
                    );
                  }
                })()}

                {allMedia.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentMediaIndex(
                          (prev) =>
                            (prev - 1 + allMedia.length) % allMedia.length,
                        )
                      }
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-20 text-sm"
                      aria-label={t("Mídia anterior")}
                    >
                      &larr;
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentMediaIndex(
                          (prev) => (prev + 1) % allMedia.length,
                        )
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all z-20 text-sm"
                      aria-label={t("Próxima mídia")}
                    >
                      &rarr;
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                      {allMedia.map((_, idx) => (
                        <div
                          key={idx}
                          className={`w-1.5 h-1.5 rounded-full transition-all shadow-xs ${
                            idx === currentMediaIndex % allMedia.length
                              ? "bg-white scale-125"
                              : "bg-white/40"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : mediaError ? (
              <div className="aspect-4/5 max-h-72 rounded-xl border border-red-200 flex flex-col items-center justify-center text-red-500 bg-red-50/40 space-y-1">
                <FileText className="w-6 h-6 text-red-300" />
                <span className="text-[10px] font-bold uppercase">
                  {t("Erro de Mídia")}
                </span>
                <span className="text-[9px] text-red-400 text-center px-4">
                  {t(
                    "Não foi possível carregar a imagem. Verifique o post original.",
                  )}
                </span>
              </div>
            ) : (
              <div className="aspect-4/5 max-h-72 rounded-xl border border-dashed border-purple-200 flex flex-col items-center justify-center text-purple-400 bg-purple-50/20 space-y-1">
                <FileText className="w-6 h-6 text-purple-300" />
                <span className="text-[10px] font-bold uppercase">
                  {t("Sem Mídia Anexada")}
                </span>
              </div>
            )}

            <div className="pt-2 border-t border-purple-50">
              <h4 className="text-xs font-bold text-gray-900 truncate">
                {selectedApprovedPost?.theme || t("Post Selecionado")}
              </h4>
              <p className="text-[10px] text-gray-400 line-clamp-2 mt-0.5">
                {selectedApprovedPost?.content ||
                  t("Campanha direta de mídia.")}
              </p>
            </div>
          </div>
        </div>

        {/* Tactical Details Grid (Right) */}
        <div className="space-y-3">
          {/* flex-wrap (não grid) de propósito: com grid, a última linha
              incompleta (ex: 2 cards sobrando de 5) deixa um espaço vazio
              no lugar do 3º card. Com flex + flex-grow, os cards da última
              linha esticam pra preencher a largura toda, sem buraco. */}
          <div className="flex flex-wrap gap-2.5">
            {summaryCards.map((card, idx) => (
              <div
                key={idx}
                className="flex-1 basis-[260px] min-w-[240px] bg-white border border-purple-200/60 rounded-xl p-3 shadow-xs space-y-2"
              >
                <div className="flex items-center gap-1.5 border-b pb-1.5 border-purple-50">
                  <div className={`p-1 ${card.bg} ${card.color} rounded-md`}>
                    <card.icon className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-[10px] font-bold text-gray-800 uppercase tracking-tight">
                    {card.title}
                  </h3>
                </div>
                <div className="space-y-1.5">
                  {card.details.map((detail, dIdx) => (
                    <div key={dIdx} className="flex flex-col min-w-0">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                        {detail.label}
                      </span>
                      <span
                        className="text-xs font-semibold text-gray-800 line-clamp-2 wrap-break-word"
                        title={
                          (detail as { tooltip?: string }).tooltip ??
                          (typeof detail.value === "string"
                            ? detail.value
                            : undefined)
                        }
                      >
                        {detail.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Geolocation summary card */}
            <div className="flex-1 basis-[260px] min-w-[240px] bg-white border border-purple-200/60 rounded-xl p-3 shadow-xs space-y-2">
              <div className="flex items-center gap-1.5 border-b pb-1.5 border-purple-50">
                <div className="p-1 bg-rose-50 text-rose-600 rounded-md">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <h3 className="text-[10px] font-bold text-gray-800 uppercase tracking-tight">
                  {t("Geolocalização")}
                </h3>
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-col min-w-0">
                  <span className="text-[9px] font-bold text-gray-400 uppercase">
                    {t("Locais")}
                  </span>
                  <span
                    className="text-xs font-bold text-gray-800 line-clamp-2 wrap-break-word"
                    title={customLocations.map((l: any) => l.name).join(", ")}
                  >
                    {customLocations.length > 0
                      ? [
                          ...customLocations
                            .slice(0, 3)
                            .map((l: any) => l.name),
                          customLocations.length > 3
                            ? t("+{count}", {
                                count: customLocations.length - 3,
                              })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(", ")
                      : t("Brasil Inteiro")}
                  </span>
                </div>
                {customLocations.length > 0 && (
                  <div className="flex flex-col min-w-0">
                    <span className="text-[9px] font-bold text-gray-400 uppercase">
                      {t("Raio")}
                    </span>
                    <span className="text-xs font-bold text-gray-800">
                      {t("{radius} km", { radius: radiusKm })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Faturamento Notice */}
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-2.5 px-3 flex items-center gap-2.5">
            <Smartphone className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-[10px] text-amber-800 font-medium">
              {t(
                "Os dados serão sincronizados com o {platform}. Certifique-se de que a forma de pagamento está ativa.",
                { platform: adsManagerLabel },
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step4_Review;
